import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledDataGrid } from '../UserRequestsStyles';
import { buildIntakeRequestColumns } from './columns';
import { IntakeRequest } from '../types';
import { GridSortModel, ToolbarPropsOverrides } from '@mui/x-data-grid';
import UserRequestsToolbar from '../UserRequestsToolbar';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../../../shared/hooks/usePagination';
import {
  useIntakeRequests,
  mapIntakeSortFieldToBackend,
} from '../hooks/useIntakeRequests';
import { useUserDateFormatter } from '../hooks';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../shared/api/getApiErrorMessage';

const IntakeRequests = () => {
  const navigate = useNavigate();
  const { formatDateTime: formatDate } = useUserDateFormatter();
  const { enqueueSnackbar } = useSnackbar();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    filters,
    quickFilter,
    handleFilterModelChange,
    handlePagination,
  } = usePagination({ initialPageSize: 10 });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'status', sort: 'asc' },
  ]);

  const intakeSort = useMemo(
    () =>
      sortModel
        .filter(item => item.field && item.sort)
        .flatMap(item => {
          const direction = item.sort === 'asc' ? ('ASC' as const) : ('DESC' as const);
          return mapIntakeSortFieldToBackend(item.field, direction);
        }),
    [sortModel],
  );

  const {
    data: intakeRequests,
    isLoading,
    isFetching,
    isError,
    error,
  } = useIntakeRequests({
    pageSize,
    page: pageIndex,
    filters,
    search: quickFilter,
    sort: intakeSort,
  });

  const rows = useMemo<IntakeRequest[]>(
    () => intakeRequests?.elements || [],
    [intakeRequests],
  );
  const totalRows = intakeRequests?.total || 0;

  useEffect(() => {
    if (!isError) return;

    enqueueSnackbar(getApiErrorMessage(error, 'Failed to load intake requests'), {
      variant: 'error',
    });
  }, [enqueueSnackbar, error, isError]);

  const renderToolbar = useCallback(
    (props: ToolbarPropsOverrides) => (
      <UserRequestsToolbar
        {...props}
        total={totalRows}
        label="Intake Requests"
      />
    ),
    [totalRows],
  );

  return (
    <StyledDataGrid
      rows={rows}
      columns={buildIntakeRequestColumns(formatDate)}
      loading={isLoading || isFetching}
      pageSizeOptions={[10, 25, 50]}
      paginationMode="server"
      filterMode="server"
      sortingMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={handlePagination}
      rowCount={totalRows}
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      slots={{ toolbar: renderToolbar }}
      getRowClassName={() => 'clickable'}
      showToolbar
      disableRowSelectionOnClick
      onRowClick={params =>
        navigate(
          `/user-requests/intake/${params.row.id}?type=${encodeURIComponent(params.row.requestType)}`,
        )
      }
    />
  );
};

export default IntakeRequests;
